/**
 * Sentry Error Tracking Configuration
 *
 * Initializes Sentry for error tracking in production.
 * Requires VITE_SENTRY_DSN environment variable to be set.
 *
 * To enable:
 * 1. Install: npm install @sentry/react
 * 2. Set VITE_SENTRY_DSN in your .env file
 * 3. Import and call initSentry() in main.tsx before ReactDOM.render
 */

import { logger } from './logger.js';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const ENVIRONMENT = import.meta.env.MODE || 'development';
const RELEASE = (import.meta.env.VITE_APP_VERSION as string) || '1.0.0';

let isInitialized = false;
/** Narrow structural view of @sentry/react — the real module's signatures are
 *  wider (SeverityLevel unions etc.); runtime accepts these plain shapes. */
interface SentryLike {
  init: (options: Record<string, unknown>) => void;
  captureException: (error: unknown, context?: Record<string, unknown>) => void;
  captureMessage: (message: string, level?: string) => void;
  setUser: (user: Record<string, unknown> | null) => void;
  addBreadcrumb: (breadcrumb: Record<string, unknown>) => void;
}
let SentryModule: SentryLike | null = null;

/**
 * Initialize Sentry error tracking
 * Only initializes if DSN is configured and Sentry package is installed
 */
export async function initSentry(): Promise<void> {
  if (isInitialized) return;

  if (!SENTRY_DSN) {
    if (ENVIRONMENT === 'production') {
      console.error('[Sentry] CRITICAL: No SENTRY_DSN configured in production - error tracking disabled');
    }
    return;
  }

  // Literal dynamic import so Vite bundles Sentry as a lazy chunk. The old
  // variable-specifier + @vite-ignore form bypassed Vite's static analysis,
  // which meant the bare specifier was never bundled and could not resolve in
  // the browser — client Sentry silently never initialized in production.
  try {
    SentryModule = (await import('@sentry/react')) as unknown as SentryLike;
  } catch {
    logger.info('[Sentry] Package not installed. Run: npm install @sentry/react');
    return;
  }

  if (!SentryModule) return;

  try {
    SentryModule.init({
      dsn: SENTRY_DSN,
      environment: ENVIRONMENT,
      release: `buzzbit-client@${RELEASE}`,

      // Performance monitoring
      tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

      // Session replay (optional)
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,

      // Filter out noisy errors
      ignoreErrors: [
        // Browser extensions
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',
        // Network errors
        'Network request failed',
        'Failed to fetch',
        'Load failed',
        // User-initiated navigation
        'AbortError',
        // Third-party scripts
        'Script error',
      ],

      // Don't send PII
      beforeSend(event: Record<string, unknown>) {
        // Scrub sensitive data from headers
        const request = event.request as Record<string, Record<string, unknown>> | undefined;
        if (request?.headers) {
          delete request.headers['Authorization'];
          delete request.headers['Cookie'];
        }
        return event;
      },
    });

    isInitialized = true;
    logger.info(`[Sentry] Initialized for ${ENVIRONMENT}`);
  } catch (error) {
    console.error('[Sentry] Failed to initialize:', error);
  }
}

/**
 * Capture an exception with optional context
 */
export function captureException(
  error: Error | unknown,
  context?: Record<string, unknown>
): void {
  if (SentryModule && isInitialized) {
    SentryModule.captureException(error, {
      extra: context,
    });
  } else {
    // Fallback to console for development/when Sentry not available
    console.error('[Error]', error, context);
  }
}

/**
 * Capture a message
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info'
): void {
  if (SentryModule && isInitialized) {
    SentryModule.captureMessage(message, level);
  } else {
    const logFn = level === 'error' ? logger.error : level === 'warning' ? logger.warn : logger.info;
    logFn(`[${level.toUpperCase()}] ${message}`);
  }
}

/**
 * Set user context for error tracking
 */
export function setUser(user: { id: string; email?: string; workspaceId?: string } | null): void {
  if (SentryModule && isInitialized) {
    SentryModule.setUser(user);
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string = 'app',
  data?: Record<string, unknown>
): void {
  if (SentryModule && isInitialized) {
    SentryModule.addBreadcrumb({
      message,
      category,
      data,
      level: 'info',
    });
  }
}

export default {
  init: initSentry,
  captureException,
  captureMessage,
  setUser,
  addBreadcrumb,
};
